import axios, { AxiosError, AxiosInstance, InternalAxiosRequestConfig } from 'axios';
import { storage } from '../utils/storage';
import type {
  AuthResponse,
  User,
  MenuResponse,
  Category,
  MenuItem,
  Formula,
  CustomizationGroup,
  CustomizationOption,
  Order,
  TruckStatus,
  TruckScheduleDay,
  TruckLocation,
  Location,
  LoyaltyInfo,
  LoyaltyTransaction,
  DashboardStats,
  DailyStat,
  ItemStat,
  LoyaltySettings,
} from '../types';

const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:3001/v1';

// Create axios instance
const api: AxiosInstance = axios.create({
  baseURL: API_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Create a separate instance for admin requests with no-cache headers
const adminApiInstance: AxiosInstance = axios.create({
  baseURL: API_URL,
  headers: {
    'Content-Type': 'application/json',
    'Cache-Control': 'no-cache, no-store, must-revalidate',
    'Pragma': 'no-cache',
  },
});

// Request interceptor - add auth token
const addAuthToken = (config: InternalAxiosRequestConfig) => {
  const token = storage.getAccessToken();
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
};

api.interceptors.request.use(addAuthToken, (error) => Promise.reject(error));
adminApiInstance.interceptors.request.use(addAuthToken, (error) => Promise.reject(error));

// Response interceptor - handle token refresh
const handleTokenRefresh = async (error: AxiosError) => {
  const originalRequest = error.config as InternalAxiosRequestConfig & { _retry?: boolean };

  if (error.response?.status === 401 && !originalRequest._retry) {
    originalRequest._retry = true;

    const refreshToken = storage.getRefreshToken();
    if (refreshToken) {
      try {
        const response = await axios.post(`${API_URL}/auth/refresh`, {
          refreshToken,
        });

        const { accessToken, refreshToken: newRefreshToken } = response.data;
        storage.setAccessToken(accessToken);
        storage.setRefreshToken(newRefreshToken);

        originalRequest.headers.Authorization = `Bearer ${accessToken}`;
        return api(originalRequest);
      } catch {
        storage.clearTokens();
        storage.clearUser();
        window.location.href = '/login';
      }
    }
  }

  return Promise.reject(error);
};

api.interceptors.response.use((response) => response, handleTokenRefresh);
adminApiInstance.interceptors.response.use((response) => response, handleTokenRefresh);

// ==========================================
// AUTH API
// ==========================================
export const authApi = {
  register: async (phone: string, first_name: string, last_name: string, password: string): Promise<AuthResponse> => {
    const { data } = await api.post('/auth/register', { phone, first_name, last_name, password });
    return data;
  },

  login: async (phone: string, password: string): Promise<AuthResponse> => {
    const { data } = await api.post('/auth/login', { phone, password });
    return data;
  },

  logout: async (): Promise<void> => {
    await api.post('/auth/logout');
  },

  getMe: async (): Promise<{ user: User }> => {
    const { data } = await api.get('/auth/me');
    return data;
  },

  updateMe: async (updates: Partial<User>): Promise<{ user: User }> => {
    const { data } = await api.put('/auth/me', updates);
    return data;
  },

  updateFcmToken: async (fcm_token: string): Promise<void> => {
    await api.post('/auth/fcm-token', { fcm_token });
  },

  clearFcmToken: async (): Promise<void> => {
    await api.delete('/auth/fcm-token');
  },

  verifyPhone: async (firebaseIdToken: string): Promise<{ user: User }> => {
    const { data } = await api.post('/auth/verify-phone', { firebaseIdToken });
    return data;
  },
};

// ==========================================
// MENU API
// ==========================================
export const menuApi = {
  getFullMenu: async (): Promise<MenuResponse> => {
    const { data } = await api.get('/menu');
    return data;
  },

  getCategories: async (): Promise<{ categories: Category[] }> => {
    const { data } = await api.get('/menu/categories');
    return data;
  },

  getMenuItems: async (categoryId?: number, availableOnly: boolean = true): Promise<{ items: MenuItem[] }> => {
    const params: Record<string, string> = {};
    if (categoryId) params.category_id = categoryId.toString();
    if (availableOnly) params.available_only = 'true';
    const { data } = await api.get('/menu/items', { params });
    return data;
  },

  getMenuItem: async (id: number): Promise<{ item: MenuItem }> => {
    const { data } = await api.get(`/menu/items/${id}`);
    return data;
  },

  getFormulas: async (): Promise<{ formulas: Formula[] }> => {
    const { data } = await api.get('/menu/formulas');
    return data;
  },

  getFormula: async (id: number): Promise<{ formula: Formula }> => {
    const { data } = await api.get(`/menu/formulas/${id}`);
    return data;
  },

  getCustomizations: async (): Promise<{ customizations: CustomizationGroup[] }> => {
    const { data } = await api.get('/menu/customizations');
    return data;
  },
};

// ==========================================
// ORDERS API
// ==========================================
export interface CreateOrderPayload {
  items: Array<{
    menu_item_id?: number;
    formula_id?: number;
    quantity: number;
    customizations?: Array<{
      customization_option_id: number;
      quantity?: number;
      sauce_quantity?: 'light' | 'normal' | 'extra';
    }>;
    notes?: string;
  }>;
  notes?: string;
  loyalty_points_used?: number;
}

export const ordersApi = {
  createOrder: async (payload: CreateOrderPayload): Promise<{ order: Order }> => {
    const { data } = await api.post('/orders', payload);
    return data;
  },

  getMyOrders: async (params?: { status?: string; limit?: number; offset?: number }): Promise<{
    orders: Order[];
    total: number;
  }> => {
    const { data } = await api.get('/orders', { params });
    return data;
  },

  getCurrentOrder: async (): Promise<{ order: Order | null }> => {
    const { data } = await api.get('/orders/current');
    return data;
  },

  getOrder: async (id: number): Promise<{ order: Order }> => {
    const { data } = await api.get(`/orders/${id}`);
    return data;
  },

  cancelOrder: async (id: number): Promise<{ order: Order }> => {
    const { data } = await api.delete(`/orders/${id}`);
    return data;
  },
};

// ==========================================
// TRUCK API
// ==========================================
export const truckApi = {
  getStatus: async (): Promise<{
    status: TruckStatus;
    scheduled_today: boolean;
    scheduled_hours: { opening: string; closing: string } | null;
  }> => {
    const { data } = await api.get('/truck/status');
    return data;
  },

  getSchedule: async (): Promise<{ schedule: TruckScheduleDay[] }> => {
    const { data } = await api.get('/truck/schedule');
    return data;
  },

  getLocation: async (): Promise<{ location: TruckLocation | null; locations: TruckLocation[] }> => {
    const { data } = await api.get('/truck/location');
    return data;
  },
};

// ==========================================
// LOYALTY API
// ==========================================
export const loyaltyApi = {
  getLoyalty: async (): Promise<LoyaltyInfo> => {
    const { data } = await api.get('/loyalty');
    return data;
  },

  getHistory: async (params?: { limit?: number; offset?: number }): Promise<{
    transactions: LoyaltyTransaction[];
    total: number;
    current_balance: number;
  }> => {
    const { data } = await api.get('/loyalty/history', { params });
    return data;
  },

  redeemPoints: async (points: number): Promise<{
    points_to_use: number;
    discount_amount: string;
    remaining_points: number;
    message: string;
  }> => {
    const { data } = await api.post('/loyalty/redeem', { points });
    return data;
  },
};

// ==========================================
// ADMIN API
// ==========================================
export const adminApi = {
  // Menu data for admin (with no-cache to always get fresh data)
  getCategories: async (): Promise<{ categories: Category[] }> => {
    const { data } = await adminApiInstance.get('/menu/categories');
    return data;
  },

  getMenuItems: async (categoryId?: number, availableOnly: boolean = false): Promise<{ items: MenuItem[] }> => {
    const params: Record<string, string> = {};
    if (categoryId) params.category_id = categoryId.toString();
    if (availableOnly) params.available_only = 'true';
    const { data } = await adminApiInstance.get('/menu/items', { params });
    return data;
  },

  // Truck Status
  updateTruckStatus: async (payload: {
    is_open?: boolean;
    custom_message?: string;
    opening_time?: string;
    closing_time?: string;
  }): Promise<{ status: TruckStatus }> => {
    const { data } = await adminApiInstance.put('/admin/truck/status', payload);
    return data;
  },

  // Menu Categories
  createCategory: async (payload: Partial<Category>): Promise<{ category: Category }> => {
    const { data } = await adminApiInstance.post('/admin/menu/categories', payload);
    return data;
  },

  updateCategory: async (id: number, payload: Partial<Category>): Promise<{ category: Category }> => {
    const { data } = await adminApiInstance.put(`/admin/menu/categories/${id}`, payload);
    return data;
  },

  deleteCategory: async (id: number): Promise<void> => {
    await adminApiInstance.delete(`/admin/menu/categories/${id}`);
  },

  // Upload Image
  uploadImage: async (file: File): Promise<{ url: string; filename: string }> => {
    const formData = new FormData();
    formData.append('image', file);
    const { data } = await adminApiInstance.post('/admin/upload', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    return data;
  },

  // Menu Items
  createMenuItem: async (payload: Partial<MenuItem>): Promise<{ item: MenuItem }> => {
    const { data } = await adminApiInstance.post('/admin/menu/items', payload);
    return data;
  },

  updateMenuItem: async (id: number, payload: Partial<MenuItem>): Promise<{ item: MenuItem }> => {
    const { data } = await adminApiInstance.put(`/admin/menu/items/${id}`, payload);
    return data;
  },

  deleteMenuItem: async (id: number): Promise<void> => {
    await adminApiInstance.delete(`/admin/menu/items/${id}`);
  },

  // Formulas
  createFormula: async (payload: Partial<Formula>): Promise<{ formula: Formula }> => {
    const { data } = await adminApiInstance.post('/admin/menu/formulas', payload);
    return data;
  },

  updateFormula: async (id: number, payload: Partial<Formula>): Promise<{ formula: Formula }> => {
    const { data } = await adminApiInstance.put(`/admin/menu/formulas/${id}`, payload);
    return data;
  },

  deleteFormula: async (id: number): Promise<void> => {
    await adminApiInstance.delete(`/admin/menu/formulas/${id}`);
  },

  // Orders
  getAllOrders: async (params?: { status?: string; date?: string; limit?: number; offset?: number }): Promise<{
    orders: Order[];
    total: number;
  }> => {
    const { data } = await adminApiInstance.get('/admin/orders', { params });
    return data;
  },

  getPendingOrders: async (): Promise<{ orders: Order[] }> => {
    const { data } = await adminApiInstance.get('/admin/orders/pending');
    return data;
  },

  updateOrderStatus: async (id: number, payload: {
    status: string;
    estimated_time?: number;
  }): Promise<{ order: Order }> => {
    const { data } = await adminApiInstance.put(`/admin/orders/${id}/status`, payload);
    return data;
  },

  // Loyalty
  getLoyaltySettings: async (): Promise<{ settings: LoyaltySettings }> => {
    const { data } = await adminApiInstance.get('/admin/loyalty/settings');
    return data;
  },

  updateLoyaltySettings: async (payload: Partial<LoyaltySettings>): Promise<{ settings: LoyaltySettings }> => {
    const { data } = await adminApiInstance.put('/admin/loyalty/settings', payload);
    return data;
  },

  adjustPoints: async (payload: { user_id: number; points: number; description?: string }): Promise<void> => {
    await adminApiInstance.post('/admin/loyalty/adjust', payload);
  },

  // Notifications
  broadcastNotification: async (payload: { title: string; body: string }): Promise<{
    stats: { total_recipients: number; success: number; failure: number };
  }> => {
    const { data } = await adminApiInstance.post('/admin/notifications/broadcast', payload);
    return data;
  },

  sendNotification: async (payload: { user_id: number; title: string; body: string }): Promise<{ success: boolean }> => {
    const { data } = await adminApiInstance.post('/admin/notifications/send', payload);
    return data;
  },

  // Stats
  getDashboardStats: async (): Promise<DashboardStats> => {
    const { data } = await adminApiInstance.get('/admin/stats/dashboard');
    return data;
  },

  getDailyStats: async (params?: { period?: 'week' | 'month'; start_date?: string; end_date?: string }): Promise<{ stats: DailyStat[] }> => {
    const { data } = await adminApiInstance.get('/admin/stats/daily', { params });
    return data;
  },

  getItemStats: async (params?: { start_date?: string; end_date?: string }): Promise<{ items: ItemStat[] }> => {
    const { data } = await adminApiInstance.get('/admin/stats/items', { params });
    return data;
  },

  exportStats: async (params?: { start_date?: string; end_date?: string; format?: string }): Promise<Blob> => {
    const { data } = await adminApiInstance.get('/admin/stats/export', {
      params,
      responseType: 'blob',
    });
    return data;
  },

  // Customizations
  getCustomizationGroups: async (): Promise<{ groups: CustomizationGroup[] }> => {
    const { data } = await adminApiInstance.get('/admin/customizations');
    return data;
  },

  createCustomizationGroup: async (payload: { name: string; type: string; is_required?: boolean }): Promise<{ group: CustomizationGroup }> => {
    const { data } = await adminApiInstance.post('/admin/customizations/groups', payload);
    return data;
  },

  updateCustomizationGroup: async (id: number, payload: Partial<CustomizationGroup>): Promise<{ group: CustomizationGroup }> => {
    const { data } = await adminApiInstance.put(`/admin/customizations/groups/${id}`, payload);
    return data;
  },

  deleteCustomizationGroup: async (id: number): Promise<void> => {
    await adminApiInstance.delete(`/admin/customizations/groups/${id}`);
  },

  createCustomizationOption: async (payload: { group_id: number; name: string; price_extra?: number; is_available?: boolean }): Promise<{ option: CustomizationOption }> => {
    const { data } = await adminApiInstance.post('/admin/customizations/options', payload);
    return data;
  },

  updateCustomizationOption: async (id: number, payload: Partial<CustomizationOption>): Promise<{ option: CustomizationOption }> => {
    const { data } = await adminApiInstance.put(`/admin/customizations/options/${id}`, payload);
    return data;
  },

  deleteCustomizationOption: async (id: number): Promise<void> => {
    await adminApiInstance.delete(`/admin/customizations/options/${id}`);
  },

  updateMenuItemCustomizations: async (itemId: number, customizationOptionIds: number[]): Promise<{ item: MenuItem }> => {
    const { data } = await adminApiInstance.put(`/admin/menu/items/${itemId}/customizations`, {
      customization_option_ids: customizationOptionIds,
    });
    return data;
  },

  // Locations
  getLocations: async (): Promise<{ locations: Location[] }> => {
    const { data } = await adminApiInstance.get('/admin/locations');
    return data;
  },

  createLocation: async (payload: Partial<Location>): Promise<{ location: Location }> => {
    const { data } = await adminApiInstance.post('/admin/locations', payload);
    return data;
  },

  updateLocation: async (id: number, payload: Partial<Location>): Promise<{ location: Location }> => {
    const { data } = await adminApiInstance.put(`/admin/locations/${id}`, payload);
    return data;
  },

  deleteLocation: async (id: number): Promise<void> => {
    await adminApiInstance.delete(`/admin/locations/${id}`);
  },
};

export default api;
